import { useEffect, useState } from "react";
import { Compass, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DashboardLayout from "@/components/DashboardLayout";
import { FadeIn } from "@/components/Shared";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function ExplorerContent() {
    const [options, setOptions] = useState({ degrees: [], specializations: [] });
    const [form, setForm] = useState({ degree: "", specialization: "", interests: "", year: "" });
    const [roadmap, setRoadmap] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/career-paths/options").then((r) => setOptions(r.data)).catch(() => {});
    }, []);

    const generate = async () => {
        if (!form.degree || !form.specialization) return;
        setBusy(true);
        try {
            const q = new URLSearchParams(form);
            const { data } = await api.get(`/career-paths/roadmap?${q.toString()}`);
            setRoadmap(data);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mx-auto max-w-5xl" data-testid="career-path-explorer">
            <h1 className="font-display text-3xl font-bold tracking-tight">Career Path Explorer</h1>
            <p className="mt-2 text-base text-muted-foreground">See where your learning can take you. Select your degree, specialization, interests and current year.</p>

            <div className="card-soft mt-8 grid items-end gap-5 p-6 sm:grid-cols-2 lg:grid-cols-5">
                <div className="space-y-2">
                    <Label>Degree</Label>
                    <Select value={form.degree} onValueChange={(v) => setForm({ ...form, degree: v })}>
                        <SelectTrigger data-testid="career-degree-select" className="rounded-lg"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{options.degrees.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label>Specialization</Label>
                    <Select value={form.specialization} onValueChange={(v) => setForm({ ...form, specialization: v })}>
                        <SelectTrigger data-testid="career-specialization-select" className="rounded-lg"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{options.specializations.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label>Interests</Label>
                    <Input data-testid="career-interests-input" placeholder="e.g. robotics, NLP" value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })} className="rounded-lg" />
                </div>
                <div className="space-y-2">
                    <Label>Current year</Label>
                    <Select value={form.year} onValueChange={(v) => setForm({ ...form, year: v })}>
                        <SelectTrigger data-testid="career-year-select" className="rounded-lg"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{["1", "2", "3", "4"].map((y) => <SelectItem key={y} value={y}>Year {y}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <Button data-testid="generate-roadmap-button" disabled={!form.degree || !form.specialization || busy} className="rounded-full" onClick={generate}>
                    <Compass className="mr-2 h-4 w-4" />{busy ? "Building..." : "Build Roadmap"}
                </Button>
            </div>

            {roadmap && (
                <div className="mt-12" data-testid="roadmap-result">
                    <h2 className="font-display text-2xl font-semibold">{roadmap.degree} → {roadmap.specialization}</h2>
                    <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{roadmap.note}</p>
                    <div className="mt-10 space-y-0">
                        {roadmap.years.map((y, i) => (
                            <FadeIn key={y.year} delay={i * 0.1}>
                                <div className="flex flex-col items-center">
                                    <div className={`card-soft w-full max-w-2xl p-6 ${y.current ? "border-primary ring-2 ring-primary/30" : ""}`} data-testid={`roadmap-year-${i + 1}`}>
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-display text-lg font-semibold uppercase tracking-wide text-primary">{y.year}</h3>
                                            {y.current && <span className="rounded-full bg-secondary/40 px-3 py-1 text-xs font-bold">You are here</span>}
                                        </div>
                                        <div className="mt-4 grid gap-2 sm:grid-cols-2">
                                            {y.items.map((item) => (
                                                <span key={item} className="rounded-lg bg-muted px-4 py-2.5 text-sm">{item}</span>
                                            ))}
                                        </div>
                                    </div>
                                    {i < roadmap.years.length && <ArrowDown className="my-3 h-5 w-5 text-primary/40" />}
                                </div>
                            </FadeIn>
                        ))}
                        <FadeIn delay={0.4}>
                            <div className="card-soft w-full max-w-2xl border-secondary/50 bg-secondary/10 p-6" data-testid="roadmap-careers">
                                <h3 className="font-display text-lg font-semibold uppercase tracking-wide">Possible Careers</h3>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {roadmap.careers.map((c) => (
                                        <span key={c} className="rounded-full bg-white px-4 py-2 text-sm font-medium shadow-sm">{c}</span>
                                    ))}
                                </div>
                            </div>
                        </FadeIn>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function CareerPaths() {
    const { user } = useAuth();
    if (user) return <DashboardLayout title="Career Path Explorer"><ExplorerContent /></DashboardLayout>;
    return (
        <div className="bg-background">
            <Header />
            <div className="px-6 py-14"><ExplorerContent /></div>
            <Footer />
        </div>
    );
}
